import { useState, useEffect, useRef, useCallback } from 'react';
import DOMPurify from 'dompurify';
import { useReduxProject } from '@Hooks/reduxProjectHook/useReduxProject';
import { useReduxActiveUser } from '@Hooks/reduxActiveUserHook/useReduxActiveUser';
import KaiziqResolveConnectionService from '@Helpers/URLHelpers/KaiziqResolveConnectionService';
import FormAgentService from '@Helpers/URLHelpers/KaiziqResolve/FormAgentService';
import FieldPageService from '@Helpers/URLHelpers/FieldPageService';
import FieldService from '@Helpers/URLHelpers/FieldService';
import ErrorHandler from '@Root/ErrorHandler';
import {
    FileText, Users, Layout, Settings, CheckCircle2,
    ChevronRight, ChevronLeft, ChevronUp, ChevronDown, X, Plus, Trash2,
    Image as ImageIcon, GripVertical,
    AlignLeft, Hash, Calendar, Phone, Paperclip, Info, Star,
    List, CheckSquare2, ArrowLeft as BackIcon,
    Bug, MessageSquare, Wrench,
} from 'lucide-react';
import { FORM_TYPES, getStatusTemplateForFormType } from '@IssueConsolePages/SettingsPage/StatusSettings/formTypes';
import { SYSTEM_STATUS_VALUES } from '@IssueConsolePages/SettingsPage/StatusSettings/statusTemplates';
import './NewFormWizard.scss';

// ─── Constants ────────────────────────────────────────────────────────────────

const STORAGE_KEY = 'kzq_new_form_wizard_draft';

const STEPS = [
    { id: 1, label: 'Basics',    icon: FileText,      desc: 'Name & description' },
    { id: 2, label: 'Agents',    icon: Users,         desc: 'Who handles submissions' },
    { id: 3, label: 'Fields',    icon: Layout,        desc: 'Pages & form fields' },
    { id: 4, label: 'Review',    icon: CheckCircle2,  desc: 'Confirm before creating' },
];

const FIELD_TYPES = [
    { value: 'text',        label: 'Short Text',    icon: AlignLeft,    desc: 'Single or multi-line text' },
    { value: 'multiChoice', label: 'Single Select', icon: List,         desc: 'Choose one from a list' },
    { value: 'multiSelect', label: 'Multi Select',  icon: CheckSquare2, desc: 'Choose multiple from a list' },
    { value: 'number',      label: 'Number',        icon: Hash,         desc: 'Numeric input with optional range' },
    { value: 'date',        label: 'Date',          icon: Calendar,     desc: 'Date or date-time picker' },
    { value: 'phoneNumber', label: 'Phone Number',  icon: Phone,        desc: 'Phone number input' },
    { value: 'file',        label: 'File Upload',   icon: Paperclip,    desc: 'Let users attach files' },
    { value: 'rating',      label: 'Rating',        icon: Star,         desc: 'Star or scale rating' },
    { value: 'information', label: 'Information',   icon: Info,         desc: 'Display-only text block' },
];

// Mirrors FormAgentForm's initial state — every agent added anywhere in the product should
// start with every notification on, whether they're added here or from the Agents page.
const DEFAULT_AGENT_NOTIFICATION_SETTINGS = {
    settings: {
        create:   { email: true, whatsapp: true },
        update:   { email: true, whatsapp: true },
        resolve:  { email: true, whatsapp: true },
        escalate: { email: true, whatsapp: true },
    },
};

const OPTION_COLORS = [
    '#2784B9','#40c47d','#FDB16C','#b06060','#f77474',
    '#6080b0','#9a60b0','#6cb060','#b0ae60','#11888b',
];

const FORM_TYPE_ICONS = {
    ticket: FileText,
    issue: Bug,
    feedback: MessageSquare,
    contact: Phone,
    it_service: Wrench,
    simple: CheckSquare2,
};

const CREATION_STEPS = [
    'Creating your form...',
    'Uploading photo...',
    'Adding agents...',
    'Building field pages...',
    'Adding fields...',
    'Registering fields...',
    'Applying advanced settings...',
    'Wrapping up...',
];

const NOTIFICATION_CHANNELS = [
    {
        key: 'create',
        label: 'On Ticket Create',
        desc: 'Send a confirmation email when a new ticket is submitted.',
        enabledKey: 'submissionCreateEmailConfirmation',
    },
    {
        key: 'update',
        label: 'On Ticket Update',
        desc: 'Notify the submitter when their ticket is updated.',
        enabledKey: 'submissionUpdateEmailConfirmation',
    },
    {
        key: 'resolve',
        label: 'On Ticket Resolve',
        desc: 'Send a resolution email when a ticket is closed.',
        enabledKey: 'submissionResolveEmailConfirmation',
    },
];

const DEFAULT_NOTIFICATION_EMAILS = {
    create: {
        enabled: true,
        template: '<p>Hi {{toUserFullName}},</p><p>Your ticket <strong>{{ticketId}}</strong> has been created successfully.</p><p>You can track it here: <a href="{{ticketURL}}">{{ticketURL}}</a></p>',
    },
    update: {
        enabled: true,
        template: '<p>Hi {{toUserFullName}},</p><p>Your ticket <strong>{{ticketId}}</strong> has been updated. Current status: <strong>{{ticketStatus}}</strong>.</p><p>View details: <a href="{{ticketURL}}">{{ticketURL}}</a></p>',
    },
    resolve: {
        enabled: false,
        template: '<p>Hi {{toUserFullName}},</p><p>Your ticket <strong>{{ticketId}}</strong> has been resolved.</p><p>Resolution details: <a href="{{ticketURL}}">{{ticketURL}}</a></p>',
    },
};

const makeTempId = () => `tmp_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`;

const DEFAULT_DRAFT = {
    step: 1,
    basics: {
        title: '',
        description: '',
        visibility: 'public',
        accessType: 'public_open',
        imageBase64: '',
    },
    formType: FORM_TYPES[0].id,
    statusList: getStatusTemplateForFormType(FORM_TYPES[0].id).statuses.map((s, i) => ({ ...s, id: i })),
    agents: [],
    pages: [{ tempId: makeTempId(), name: 'Page 1', fields: [] }],
    advanced: {
        skip: false,
        sla: { enabled: false, responseTimeMins: 60, resolutionTimeMins: 480 },
        notificationEmails: DEFAULT_NOTIFICATION_EMAILS,
        assigneeMatrix:  { enabled: false },
        priorityMatrix:  { enabled: false },
        escalationMatrix:{ enabled: false },
    },
};

// ─── Main wizard ──────────────────────────────────────────────────────────────

export default function NewFormWizard({ onClose, onComplete }) {

    const { currentProjectId, getSimpleUsersByProjectId } = useReduxProject();
    useReduxActiveUser();
    const errorHandler = new ErrorHandler();

    // Services (stable refs — no re-instantiation on render)
    const svcForm      = useRef(new KaiziqResolveConnectionService());
    const svcAgent     = useRef(new FormAgentService());
    const svcFieldPage = useRef(new FieldPageService());
    const svcField     = useRef(new FieldService());

    // ── Draft (localStorage-backed) ──
    // Merged against DEFAULT_DRAFT (not returned as-is) so drafts saved before a field was
    // added — like formType/statusList — don't crash the wizard on load.
    const [draft, setDraft] = useState(() => {
        try {
            const raw = localStorage.getItem(STORAGE_KEY);
            if (raw) {
                const parsed = JSON.parse(raw);
                return {
                    ...DEFAULT_DRAFT,
                    ...parsed,
                    basics: { ...DEFAULT_DRAFT.basics, ...(parsed.basics || {}) },
                    advanced: { ...DEFAULT_DRAFT.advanced, ...(parsed.advanced || {}) },
                    statusList: Array.isArray(parsed.statusList) && parsed.statusList.length > 0
                        ? parsed.statusList
                        : DEFAULT_DRAFT.statusList,
                };
            }
        } catch (_) {}
        return DEFAULT_DRAFT;
    });

    const [step, setStep] = useState(draft.step || 1);

    // ── UI state ──
    const [agentPool, setAgentPool]         = useState([]);
    const [agentSearch, setAgentSearch]     = useState('');
    const [loadingAgents, setLoadingAgents] = useState(false);
    const [newField, setNewField]           = useState(null);   // { pageIdx, label, type, required }
    const [creating, setCreating]           = useState(false);
    const [creationStepIdx, setCreationStepIdx] = useState(0);
    const [creationError, setCreationError] = useState(null);
    const [imagePreview, setImagePreview]   = useState(draft.basics.imageBase64 || '');

    useEffect(() => {
        setDraft(d => {
            if (d?.advanced?.notificationEmails?.create?.template !== undefined) return d;
            return {
                ...d,
                advanced: {
                    ...d.advanced,
                    notificationEmails: {
                        ...DEFAULT_NOTIFICATION_EMAILS,
                        ...(d?.advanced?.notificationEmails || {}),
                    },
                },
            };
        });
    }, []);

    // ── Persist draft on every change ──
    useEffect(() => {
        try { localStorage.setItem(STORAGE_KEY, JSON.stringify({ ...draft, step })); }
        catch (_) {}
    }, [draft, step]);

    // ── Load agent pool on step 2 ──
    useEffect(() => {
        if (step !== 2 || !currentProjectId) return;
        setLoadingAgents(true);
        getSimpleUsersByProjectId(currentProjectId)
            .then(list => setAgentPool(Array.isArray(list) ? list : []))
            .catch(() => setAgentPool([]))
            .finally(() => setLoadingAgents(false));
    }, [step, currentProjectId]);

    // ── Helpers ──
    const updateBasics   = useCallback((patch) => setDraft(d => ({ ...d, basics:   { ...d.basics,   ...patch } })), []);

    // ── Status workflow (part of the Basics step) ──
    const [statusColorPickerIdx, setStatusColorPickerIdx] = useState(null);

    const selectFormType = (id) => setDraft(d => ({
        ...d,
        formType: id,
        statusList: getStatusTemplateForFormType(id).statuses.map((s, i) => ({ ...s, id: i })),
    }));

    const updateStatusTitle = (idx, title) => setDraft(d => ({
        ...d,
        statusList: d.statusList.map((x, i) => i === idx ? { ...x, title: DOMPurify.sanitize(title) } : x),
    }));

    const updateStatusColor = (idx, color) => setDraft(d => ({
        ...d,
        statusList: d.statusList.map((x, i) => i === idx ? { ...x, color } : x),
    }));

    const moveStatus = (idx, dir) => setDraft(d => {
        const arr = [...d.statusList];
        const target = idx + dir;
        if (target < 0 || target >= arr.length) return d;
        [arr[idx], arr[target]] = [arr[target], arr[idx]];
        return { ...d, statusList: arr.map((x, i) => ({ ...x, id: i })) };
    });

    const removeStatus = (idx) => setDraft(d => ({
        ...d,
        statusList: d.statusList.filter((_, i) => i !== idx),
    }));

    const addCustomStatus = () => setDraft(d => ({
        ...d,
        statusList: [...d.statusList, {
            id: d.statusList.length,
            title: 'New Stage',
            value: `custom_${Date.now()}`,
            color: OPTION_COLORS[d.statusList.length % OPTION_COLORS.length],
        }],
    }));

    const clearDraft = () => {
        localStorage.removeItem(STORAGE_KEY);
        setDraft(DEFAULT_DRAFT);
        setStep(1);
        setImagePreview('');
        setNewField(null);
        setCreating(false);
        setCreationError(null);
        setCreationStepIdx(0);
    };

    const handleClose = () => { onClose(); };

    const hasDraft = (() => {
        try {
            const raw = localStorage.getItem(STORAGE_KEY);
            if (!raw) return false;
            const saved = JSON.parse(raw);
            return !!(saved?.basics?.title?.trim());
        } catch (_) { return false; }
    })();

    // ── Navigation ──
    const canAdvance = () => {
        if (step === 1) return draft.basics.title.trim().length >= 2;
        return true;
    };

    const handleNext = () => { if (canAdvance()) setStep(s => Math.min(s + 1, 4)); };
    const handleBack = () => setStep(s => Math.max(s - 1, 1));

    // ── Image upload ──
    const handleImageChange = (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        if (file.size > 1024 * 1024) { alert('Image must be under 1 MB.'); return; }
        const reader = new FileReader();
        reader.onload = (ev) => {
            const b64 = ev.target.result;
            setImagePreview(b64);
            updateBasics({ imageBase64: b64 });
        };
        reader.readAsDataURL(file);
    };

    // ── Agent step ──
    const toggleAgent = (user) => {
        const uid = user.id;
        setDraft(d => {
            const exists = d.agents.some(a => a.userId === uid);
            return {
                ...d,
                agents: exists
                    ? d.agents.filter(a => a.userId !== uid)
                    : [...d.agents, {
                        userId:   uid,
                        fullName: user.fullName || user.userName || `User ${uid}`,
                        userName: user.userName || '',
                    }],
            };
        });
    };

    // ── Fields step ──
    const addPage = () => setDraft(d => ({
        ...d,
        pages: [...d.pages, { tempId: makeTempId(), name: `Page ${d.pages.length + 1}`, fields: [] }],
    }));

    const removePage = (pageIdx) => setDraft(d => ({
        ...d,
        pages: d.pages.filter((_, i) => i !== pageIdx),
    }));

    const updatePageName = (pageIdx, name) => setDraft(d => ({
        ...d,
        pages: d.pages.map((p, i) => i === pageIdx ? { ...p, name } : p),
    }));

    const isChoiceType = (type) => type === 'multiChoice' || type === 'multiSelect';

    const addNewFieldOption = () => setNewField(f => ({
        ...f,
        options: [...f.options, { title: `Option ${f.options.length + 1}`, color: OPTION_COLORS[f.options.length % OPTION_COLORS.length] }],
    }));

    const updateNewFieldOption = (idx, title) => setNewField(f => ({
        ...f,
        options: f.options.map((o, i) => i === idx ? { ...o, title } : o),
    }));

    const removeNewFieldOption = (idx) => setNewField(f => ({
        ...f,
        options: f.options.filter((_, i) => i !== idx),
    }));

    const commitField = () => {
        if (!newField || !newField.title.trim()) return;

        const rules = [
            { id: 0, type: 'required', value: newField.required },
            { id: 1, type: 'default',  value: '' },
            { id: 2, type: 'width',    value: newField.maxLength || 250 },
        ];
        if (newField.type === 'number') {
            rules.push({ id: 3, type: 'digitWidth', value: 10 });
            if (newField.numberMin !== '') rules.push({ id: 4, type: 'rangeMin', value: newField.numberMin });
            if (newField.numberMax !== '') rules.push({ id: 5, type: 'rangeMax', value: newField.numberMax });
        }

        const options = isChoiceType(newField.type)
            ? newField.options.map((opt, i) => ({
                id: i, title: opt.title, value: i + 1,
                color: opt.color || OPTION_COLORS[i % OPTION_COLORS.length],
                icon: 'none', rules: [],
              }))
            : [];

        const fieldToAdd = {
            tempId: makeTempId(),
            title:    newField.title.trim(),
            type:     newField.type,
            required: newField.required,
            rules,
            options,
        };

        setDraft(d => ({
            ...d,
            pages: d.pages.map((p, i) =>
                i === newField.pageIdx
                    ? { ...p, fields: [...p.fields, fieldToAdd] }
                    : p
            ),
        }));
        setNewField(null);
    };

    const removeField = (pageIdx, fieldIdx) => setDraft(d => ({
        ...d,
        pages: d.pages.map((p, i) =>
            i === pageIdx ? { ...p, fields: p.fields.filter((_, fi) => fi !== fieldIdx) } : p
        ),
    }));

    // ── Creation ──
    const tick = (idx) => setCreationStepIdx(idx);

    const handleCreate = async () => {
        setCreating(true);
        setCreationError(null);
        setCreationStepIdx(0);

        try {
            // 1. Create form
            tick(0);
            let formRes = await svcForm.current.createKaiziqResolveConnection({
                projectId:  currentProjectId,
                title:      draft.basics.title.trim(),
                // Visibility/access pickers are hidden until the other modes are ready, so always
                // send the defaults (a saved draft may still carry an older choice).
                visibility: DEFAULT_DRAFT.basics.visibility,
                accessType: DEFAULT_DRAFT.basics.accessType,
                formType:   draft.formType,
                initialStatuses: draft.statusList.map((s, i) => ({ title: s.title, value: s.value, color: s.color, id: i })),
            });
            const form = JSON.parse(formRes.body);
            const formId = form.id;

            // The backend auto-creates the first field page. Extract its ID from the
            // form details so we can add fields directly to it without a separate API call.
            let defaultPageId = null;
            try {
                const formDetails = JSON.parse(form.details);
                const autoPages = formDetails?.fields?.formPages;
                if (Array.isArray(autoPages) && autoPages.length > 0) {
                    defaultPageId = autoPages[0];
                }
            } catch (_) {}

            // 2. Upload photo
            tick(1);
            if (draft.basics.imageBase64) {
                try {
                    await svcForm.current.updateKaiziqResolveConnectionPhoto(formId, draft.basics.imageBase64);
                } catch (_) {}
            }

            // 3. Add agents
            tick(2);
            for (const agent of draft.agents) {
                try {
                    await svcAgent.current.createFormAgent({
                        kaiziqResolveConnectionId: formId,
                        userId:    agent.userId,
                        projectId: currentProjectId,
                        details:   JSON.stringify(DEFAULT_AGENT_NOTIFICATION_SETTINGS),
                    });
                } catch (_) {}
            }

            // 4. Resolve page IDs:
            //    - Page 0 → use the auto-created page ID already in form.details.fields.formPages
            //    - Pages 1+ → createFieldPageListByTable (same as FieldsPage), then update
            //                  form details.fields.formPages to register each new page ID.
            tick(3);
            const pageIds = [defaultPageId]; // page 0 already registered in form details

            // Parse the live form details so we can patch formPages after each new page
            let liveDetails = {};
            try { liveDetails = JSON.parse(form.details); } catch (_) {}

            for (let i = 1; i < draft.pages.length; i++) {
                try {
                    const pageRes = await svcFieldPage.current.createFieldPageListByTable(
                        'kaiziqResolveConnection',
                        formId,
                        currentProjectId,
                    );
                    const created = JSON.parse(pageRes.body);
                    pageIds.push(created.id);

                    // Mirror what FieldsPage does: push the new page ID into formPages
                    // and persist it so the form knows about this page.
                    if (!liveDetails?.fields?.formPages) {
                        liveDetails = { ...liveDetails, fields: { ...(liveDetails.fields || {}), formPages: [defaultPageId] } };
                    }
                    liveDetails.fields.formPages.push(created.id);
                    await svcForm.current.updateKaiziqResolveConnectionDetails(
                        formId,
                        JSON.stringify(liveDetails),
                    );
                } catch (_) {
                    pageIds.push(null);
                }
            }

            // 5. Create fields — collect new field IDs per page so we can register them
            tick(4);
            const fieldIdsByPage = draft.pages.map(() => []); // parallel array to pageIds

            for (let pi = 0; pi < draft.pages.length; pi++) {
                const page = draft.pages[pi];
                const fieldPageId = pageIds[pi];
                if (!fieldPageId) continue;
                for (let fi = 0; fi < page.fields.length; fi++) {
                    const field = page.fields[fi];
                    try {
                        const fieldRes = await svcField.current.createField({
                            projectId:        currentProjectId,
                            tableType:        'kaiziqResolveConnection',
                            tableId:          formId,
                            fieldPageId:      fieldPageId,
                            title:            field.title,
                            type:             field.type,
                            rules:            JSON.stringify(field.rules || []),
                            options:          JSON.stringify(field.options || []),
                            filterAttribute1: 'form',
                        });
                        const createdField = JSON.parse(fieldRes.body);
                        if (createdField?.id) fieldIdsByPage[pi].push(createdField.id);
                    } catch (_) {}
                }
            }

            // 6. Register fields — mirrors FieldForm's post-create steps:
            //    a) Update each FieldPage.fields list with the new field IDs
            //    b) Update form details.fields.form list + details.fields.fieldHistory
            tick(5);
            for (let pi = 0; pi < pageIds.length; pi++) {
                const fieldPageId = pageIds[pi];
                const newIds = fieldIdsByPage[pi];
                if (!fieldPageId || newIds.length === 0) continue;
                try {
                    const pageRes = await svcFieldPage.current.getFieldPageById(fieldPageId);
                    const pageObj  = JSON.parse(pageRes.body);
                    const existing = JSON.parse(pageObj.fields || '[]');
                    pageObj.fields = JSON.stringify([...existing, ...newIds]);
                    await svcFieldPage.current.updateFieldPage(pageObj);
                } catch (_) {}
            }

            const allNewFieldIds = fieldIdsByPage.flat();
            if (allNewFieldIds.length > 0) {
                try {
                    const formList    = liveDetails.fields?.form        || [];
                    const fieldHistory = liveDetails.fields?.fieldHistory || {};
                    allNewFieldIds.forEach(id => {
                        formList.push(id);
                        fieldHistory[id] = { current: id, fieldHistory: [id] };
                    });
                    liveDetails.fields.form         = formList;
                    liveDetails.fields.fieldHistory = fieldHistory;
                    await svcForm.current.updateKaiziqResolveConnectionDetails(formId, JSON.stringify(liveDetails));
                } catch (_) {}
            }

            // 7. Apply advanced settings — mutate liveDetails to preserve fields/formPages
            tick(6);
            if (!draft.advanced.skip) {
                if (draft.advanced.sla.enabled) liveDetails.sla = draft.advanced.sla;
                if (draft.advanced.assigneeMatrix.enabled)   liveDetails.assigneeMatrixEnabled   = true;
                if (draft.advanced.priorityMatrix.enabled)   liveDetails.priorityMatrixEnabled   = true;
                if (draft.advanced.escalationMatrix.enabled) liveDetails.escalationMatrixEnabled = true;

                const settingsObj = { ...(liveDetails.settings || {}) };
                const defaultsObj = { ...(settingsObj.defaults || {}) };
                const notificationTemplatesObj = { ...(settingsObj.notificationTemplates || {}) };
                const notificationEmailTemplates = { ...(notificationTemplatesObj.email || {}) };

                NOTIFICATION_CHANNELS.forEach(({ key, enabledKey }) => {
                    defaultsObj[enabledKey] = !!draft.advanced.notificationEmails[key]?.enabled;
                    if (draft.advanced.notificationEmails[key]?.template?.trim()) {
                        notificationEmailTemplates[key] = draft.advanced.notificationEmails[key].template;
                    }
                });

                notificationTemplatesObj.email = notificationEmailTemplates;
                settingsObj.defaults = defaultsObj;
                settingsObj.notificationTemplates = notificationTemplatesObj;
                liveDetails.settings = settingsObj;

                if (draft.basics.description.trim()) {
                    liveDetails.description = draft.basics.description.trim();
                }

                try {
                    await svcForm.current.updateKaiziqResolveConnectionDetails(formId, JSON.stringify(liveDetails));
                } catch (_) {}
            }

            tick(7);
            await new Promise(r => setTimeout(r, 800));

            clearDraft();
            onComplete();
            onClose();

        } catch (err) {
            errorHandler.handleError(err);
            setCreationError('Something went wrong. Please try again.');
            setCreating(false);
        }
    };

    // ── Rendering ─────────────────────────────────────────────────────────────
    const cn = 'newFormWizard';

    const filteredPool = agentPool.filter(a => {
        const q = agentSearch.toLowerCase();
        return !q || (a.fullName || '').toLowerCase().includes(q) || (a.userName || '').toLowerCase().includes(q);
    });

    return (
        <div className={cn}>
            {/* Creating overlay */}
            {creating && (
                <div className={cn + '__creating'}>
                    <div className={cn + '__creating__card'}>
                        <div className={cn + '__creating__ring'} />
                        <p className={cn + '__creating__status'}>
                            {creationError || CREATION_STEPS[Math.min(creationStepIdx, CREATION_STEPS.length - 1)]}
                        </p>
                        {creationError && (
                            <button className={cn + '__creating__retry'} onClick={() => setCreating(false)}>
                                Go back
                            </button>
                        )}
                        <ol className={cn + '__creating__steps'}>
                            {CREATION_STEPS.map((label, idx) => (
                                <li key={idx} className={cn + '__creating__step' + (idx < creationStepIdx ? ' --done' : idx === creationStepIdx ? ' --active' : '')}>
                                    <span className={cn + '__creating__step__dot'} />
                                    {label}
                                </li>
                            ))}
                        </ol>
                    </div>
                </div>
            )}

            {/* Sidebar */}
            <aside className={cn + '__sidebar'}>
                <div className={cn + '__sidebar__brand'}>
                    <FileText size={18} />
                    <span>New Form</span>
                </div>
                {hasDraft && (
                    <div className={cn + '__sidebar__draft-badge'}>Draft saved</div>
                )}
                <nav className={cn + '__sidebar__steps'}>
                    {STEPS.map(s => {
                        const Icon = s.icon;
                        const done = step > s.id;
                        const active = step === s.id;
                        return (
                            <div
                                key={s.id}
                                className={cn + '__sidebar__step' + (active ? ' --active' : '') + (done ? ' --done' : '')}
                                onClick={() => { if (done || active) setStep(s.id); }}
                            >
                                <div className={cn + '__sidebar__step__icon'}>
                                    {done ? <CheckCircle2 size={16} /> : <Icon size={16} />}
                                </div>
                                <div className={cn + '__sidebar__step__text'}>
                                    <span className={cn + '__sidebar__step__label'}>{s.label}</span>
                                    <span className={cn + '__sidebar__step__desc'}>{s.desc}</span>
                                </div>
                            </div>
                        );
                    })}
                </nav>
                <button className={cn + '__sidebar__discard'} onClick={() => { clearDraft(); handleClose(); }}>
                    <Trash2 size={14} /> Discard draft
                </button>
            </aside>

            {/* Main panel */}
            <div className={cn + '__main'}>
                <div className={cn + '__main__header'}>
                    <div className={cn + '__main__header__title'}>
                        {STEPS[step - 1].label}
                        <span className={cn + '__main__header__sub'}>{STEPS[step - 1].desc}</span>
                    </div>
                    <button className={cn + '__main__header__close'} onClick={handleClose}>
                        <X size={20} />
                    </button>
                </div>

                <div className={cn + '__main__body'}>

                    {/* ── Step 1: Basics ── */}
                    {step === 1 && (
                        <div className={cn + '__step'}>
                            <div className={cn + '__step__row'}>
                                <label className={cn + '__label'}>Form Name <em>*</em></label>
                                <input
                                    className={cn + '__input'}
                                    placeholder="e.g. Customer Support Form"
                                    value={draft.basics.title}
                                    maxLength={200}
                                    onChange={e => updateBasics({ title: DOMPurify.sanitize(e.target.value) })}
                                />
                            </div>

                            <div className={cn + '__step__row'}>
                                <label className={cn + '__label'}>Description</label>
                                <textarea
                                    className={cn + '__textarea'}
                                    rows={3}
                                    placeholder="Briefly describe what this form is for..."
                                    value={draft.basics.description}
                                    onChange={e => updateBasics({ description: DOMPurify.sanitize(e.target.value) })}
                                />
                            </div>

                            <div className={cn + '__step__row'}>
                                <label className={cn + '__label'}>Form Type</label>
                                <p className={cn + '__hint'} style={{ margin: '0 0 0.5rem' }}>
                                    Sets up the ticket status workflow below. You can rename, recolour, reorder, or
                                    add stages — everything can be changed later from Status Settings.
                                </p>
                                <div className={cn + '__options'}>
                                    {FORM_TYPES.map(type => {
                                        const Icon = FORM_TYPE_ICONS[type.id] || FileText;
                                        return (
                                            <div
                                                key={type.id}
                                                className={cn + '__option' + (draft.formType === type.id ? ' --selected' : '')}
                                                onClick={() => selectFormType(type.id)}
                                            >
                                                <Icon size={18} />
                                                <div>
                                                    <strong>{type.label}</strong>
                                                    <span>{type.description}</span>
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>

                            <div className={cn + '__step__row'}>
                                <label className={cn + '__label'}>Status Preview</label>
                                <div className={cn + '__status-list'}>
                                    {draft.statusList.map((status, idx) => (
                                        <div key={idx} className={cn + '__status-row'}>
                                            <div className={cn + '__status-row__reorder'}>
                                                <button
                                                    type='button'
                                                    className={cn + '__icon-btn'}
                                                    disabled={idx === 0}
                                                    onClick={() => moveStatus(idx, -1)}
                                                    title='Move up'
                                                >
                                                    <ChevronUp size={13} />
                                                </button>
                                                <button
                                                    type='button'
                                                    className={cn + '__icon-btn'}
                                                    disabled={idx === draft.statusList.length - 1}
                                                    onClick={() => moveStatus(idx, 1)}
                                                    title='Move down'
                                                >
                                                    <ChevronDown size={13} />
                                                </button>
                                            </div>

                                            <div className={cn + '__status-row__swatchWrap'}>
                                                <button
                                                    type='button'
                                                    className={cn + '__status-row__swatch'}
                                                    style={{ background: status.color }}
                                                    onClick={() => setStatusColorPickerIdx(i => i === idx ? null : idx)}
                                                    title='Change colour'
                                                />
                                                {statusColorPickerIdx === idx && (
                                                    <div className={cn + '__status-color-popover'}>
                                                        {OPTION_COLORS.map(c => (
                                                            <button
                                                                key={c}
                                                                type='button'
                                                                className={cn + '__status-color-popover__dot'}
                                                                style={{ background: c }}
                                                                onClick={() => { updateStatusColor(idx, c); setStatusColorPickerIdx(null); }}
                                                            />
                                                        ))}
                                                    </div>
                                                )}
                                            </div>

                                            <input
                                                className={cn + '__input --sm'}
                                                value={status.title}
                                                maxLength={60}
                                                onChange={e => updateStatusTitle(idx, e.target.value)}
                                            />

                                            {!SYSTEM_STATUS_VALUES.has(status.value) && (
                                                <button
                                                    type='button'
                                                    className={cn + '__icon-btn --danger'}
                                                    onClick={() => removeStatus(idx)}
                                                    title='Remove status'
                                                >
                                                    <Trash2 size={14} />
                                                </button>
                                            )}
                                        </div>
                                    ))}
                                    <button type='button' className={cn + '__btn --ghost --sm'} onClick={addCustomStatus}>
                                        <Plus size={13} /> Add status
                                    </button>
                                </div>
                            </div>

                            <div className={cn + '__step__row'}>
                                <label className={cn + '__label'}>Form Photo</label>
                                <div className={cn + '__image-upload'}>
                                    {imagePreview ? (
                                        <div className={cn + '__image-preview'}>
                                            <img src={imagePreview} alt="preview" />
                                            <button onClick={() => { setImagePreview(''); updateBasics({ imageBase64: '' }); }}>
                                                <X size={14} />
                                            </button>
                                        </div>
                                    ) : (
                                        <label className={cn + '__image-upload__placeholder'}>
                                            <ImageIcon size={24} />
                                            <span>Click to upload (max 1 MB)</span>
                                            <input type="file" accept="image/*" onChange={handleImageChange} />
                                        </label>
                                    )}
                                </div>
                            </div>
                        </div>
                    )}

                    {/* ── Step 2: Agents ── */}
                    {step === 2 && (
                        <div className={cn + '__step'}>
                            <p className={cn + '__hint'}>
                                Select team members who will manage and respond to submissions for this form.
                            </p>
                            <div className={cn + '__step__row'}>
                                <label className={cn + '__label'}>Search agents</label>
                                <input
                                    className={cn + '__input'}
                                    placeholder="Filter by name..."
                                    value={agentSearch}
                                    onChange={e => setAgentSearch(e.target.value)}
                                />
                            </div>

                            {loadingAgents ? (
                                <div className={cn + '__agent-loading'}>Loading available agents...</div>
                            ) : (
                                <div className={cn + '__agent-pool'}>
                                    {filteredPool.length === 0 && (
                                        <div className={cn + '__empty'}>No agents found in this project.</div>
                                    )}
                                    {filteredPool.map(user => {
                                        const selected = draft.agents.some(a => a.userId === user.id);
                                        const name = user.fullName || user.userName || `User ${user.id}`;
                                        return (
                                            <div
                                                key={user.id}
                                                className={cn + '__agent-card' + (selected ? ' --selected' : '')}
                                                onClick={() => toggleAgent(user)}
                                            >
                                                <div className={cn + '__agent-card__avatar'}>
                                                    {name.charAt(0).toUpperCase()}
                                                </div>
                                                <div className={cn + '__agent-card__info'}>
                                                    <strong>{name}</strong>
                                                    {user.userName && <span>@{user.userName}</span>}
                                                </div>
                                                {selected && <CheckCircle2 size={18} className={cn + '__agent-card__check'} />}
                                            </div>
                                        );
                                    })}
                                </div>
                            )}

                            {draft.agents.length > 0 && (
                                <div className={cn + '__selected-agents'}>
                                    <label className={cn + '__label'}>Selected ({draft.agents.length})</label>
                                    <div className={cn + '__agent-chips'}>
                                        {draft.agents.map(a => (
                                            <div key={a.userId} className={cn + '__agent-chip'}>
                                                <span>{a.fullName || `User ${a.userId}`}</span>
                                                <button onClick={() => toggleAgent(a)}><X size={12} /></button>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>
                    )}

                    {/* ── Step 3: Fields ── */}
                    {step === 3 && (
                        <div className={cn + '__step'}>
                            <p className={cn + '__hint'}>
                                Build your form by adding pages and fields. Page 1 is created automatically.
                            </p>

                            {draft.pages.map((page, pageIdx) => (
                                <div key={page.tempId} className={cn + '__page-block'}>
                                    {/* Page header */}
                                    <div className={cn + '__page-block__header'}>
                                        <div className={cn + '__page-block__header__badge'}>{pageIdx + 1}</div>
                                        <input
                                            className={cn + '__page-name-input'}
                                            value={page.name}
                                            onChange={e => updatePageName(pageIdx, DOMPurify.sanitize(e.target.value))}
                                        />
                                        {draft.pages.length > 1 && (
                                            <button className={cn + '__icon-btn --danger'} onClick={() => removePage(pageIdx)}>
                                                <Trash2 size={14} />
                                            </button>
                                        )}
                                    </div>

                                    {/* Existing fields */}
                                    <div className={cn + '__fields-list'}>
                                        {page.fields.length === 0 && (
                                            <div className={cn + '__empty'}>No fields yet — add one below.</div>
                                        )}
                                        {page.fields.map((field, fi) => {
                                            const ft = FIELD_TYPES.find(t => t.value === field.type);
                                            const Icon = ft?.icon || AlignLeft;
                                            return (
                                                <div key={field.tempId} className={cn + '__field-row'}>
                                                    <GripVertical size={13} className={cn + '__field-row__grip'} />
                                                    <Icon size={14} className={cn + '__field-row__icon'} />
                                                    <span className={cn + '__field-row__label'}>{field.title}</span>
                                                    <span className={cn + '__field-row__type'}>{ft?.label || field.type}</span>
                                                    {field.required && <span className={cn + '__field-row__req'}>Required</span>}
                                                    {isChoiceType(field.type) && field.options?.length > 0 && (
                                                        <span className={cn + '__field-row__opts'}>{field.options.length} options</span>
                                                    )}
                                                    <button className={cn + '__icon-btn --danger'} onClick={() => removeField(pageIdx, fi)}>
                                                        <Trash2 size={13} />
                                                    </button>
                                                </div>
                                            );
                                        })}
                                    </div>

                                    {/* Add field panel */}
                                    {newField?.pageIdx === pageIdx ? (
                                        <div className={cn + '__field-panel'} style={{ margin: '0 0.75rem 0' }}>

                                            {/* Stage A: type picker */}
                                            {newField.stage === 'type' && (
                                                <>
                                                    <div className={cn + '__field-panel__header'}>
                                                        <span>Choose field type</span>
                                                        <button className={cn + '__icon-btn'} onClick={() => setNewField(null)}><X size={14} /></button>
                                                    </div>
                                                    <div className={cn + '__type-grid'}>
                                                        {FIELD_TYPES.map(ft => {
                                                            const Icon = ft.icon;
                                                            return (
                                                                <div
                                                                    key={ft.value}
                                                                    className={cn + '__type-card'}
                                                                    onClick={() => setNewField(f => ({
                                                                        ...f, type: ft.value, stage: 'config',
                                                                        options: [],
                                                                        maxLength: 250,
                                                                        numberMin: '',
                                                                        numberMax: '',
                                                                    }))}
                                                                >
                                                                    <Icon size={20} />
                                                                    <strong>{ft.label}</strong>
                                                                    <span>{ft.desc}</span>
                                                                </div>
                                                            );
                                                        })}
                                                    </div>
                                                </>
                                            )}

                                            {/* Stage B: config */}
                                            {newField.stage === 'config' && (() => {
                                                const ft = FIELD_TYPES.find(t => t.value === newField.type);
                                                const Icon = ft?.icon || AlignLeft;
                                                const needsOptions = isChoiceType(newField.type);
                                                const canCommit = newField.title.trim() &&
                                                    (!needsOptions || newField.options.length >= 1);
                                                return (
                                                    <>
                                                        <div className={cn + '__field-panel__header'}>
                                                            <button
                                                                className={cn + '__icon-btn'}
                                                                onClick={() => setNewField(f => ({ ...f, stage: 'type' }))}
                                                            >
                                                                <BackIcon size={14} />
                                                            </button>
                                                            <Icon size={15} />
                                                            <span>{ft?.label}</span>
                                                            <button className={cn + '__icon-btn'} style={{ marginLeft: 'auto' }} onClick={() => setNewField(null)}><X size={14} /></button>
                                                        </div>

                                                        <div className={cn + '__field-panel__body'}>
                                                            {/* Title */}
                                                            <div className={cn + '__field-config-row'}>
                                                                <label className={cn + '__label'}>Field label <em>*</em></label>
                                                                <input
                                                                    className={cn + '__input'}
                                                                    placeholder={`e.g. ${ft?.label} field`}
                                                                    value={newField.title}
                                                                    autoFocus
                                                                    maxLength={200}
                                                                    onChange={e => setNewField(f => ({ ...f, title: e.target.value }))}
                                                                />
                                                            </div>

                                                            {/* Required toggle (not for information) */}
                                                            {newField.type !== 'information' && (
                                                                <label className={cn + '__toggle-label'}>
                                                                    <input
                                                                        type="checkbox"
                                                                        checked={newField.required}
                                                                        onChange={e => setNewField(f => ({ ...f, required: e.target.checked }))}
                                                                    />
                                                                    Required field
                                                                </label>
                                                            )}

                                                            {/* text: max length */}
                                                            {newField.type === 'text' && (
                                                                <div className={cn + '__field-config-row'}>
                                                                    <label className={cn + '__label'}>Max character limit</label>
                                                                    <input
                                                                        type="number" min={1} max={5000}
                                                                        className={cn + '__input --sm'}
                                                                        value={newField.maxLength}
                                                                        onChange={e => setNewField(f => ({ ...f, maxLength: +e.target.value }))}
                                                                    />
                                                                </div>
                                                            )}

                                                            {/* number: min/max */}
                                                            {newField.type === 'number' && (
                                                                <div className={cn + '__field-config-row --row'}>
                                                                    <div>
                                                                        <label className={cn + '__label'}>Min</label>
                                                                        <input type="number" className={cn + '__input --sm'}
                                                                            value={newField.numberMin}
                                                                            placeholder="None"
                                                                            onChange={e => setNewField(f => ({ ...f, numberMin: e.target.value }))} />
                                                                    </div>
                                                                    <div>
                                                                        <label className={cn + '__label'}>Max</label>
                                                                        <input type="number" className={cn + '__input --sm'}
                                                                            value={newField.numberMax}
                                                                            placeholder="None"
                                                                            onChange={e => setNewField(f => ({ ...f, numberMax: e.target.value }))} />
                                                                    </div>
                                                                </div>
                                                            )}

                                                            {/* multiChoice / multiSelect: options */}
                                                            {needsOptions && (
                                                                <div className={cn + '__field-config-row'}>
                                                                    <label className={cn + '__label'}>
                                                                        Options {newField.type === 'multiChoice' && <span className={cn + '__hint-inline'}>(min 2 required)</span>}
                                                                    </label>
                                                                    <div className={cn + '__options-builder'}>
                                                                        {newField.options.map((opt, oi) => (
                                                                            <div key={oi} className={cn + '__options-builder__row'}>
                                                                                <div
                                                                                    className={cn + '__options-builder__swatch'}
                                                                                    style={{ background: opt.color }}
                                                                                />
                                                                                <input
                                                                                    className={cn + '__input --sm'}
                                                                                    value={opt.title}
                                                                                    placeholder={`Option ${oi + 1}`}
                                                                                    onChange={e => updateNewFieldOption(oi, e.target.value)}
                                                                                />
                                                                                <button
                                                                                    className={cn + '__icon-btn --danger'}
                                                                                    onClick={() => removeNewFieldOption(oi)}
                                                                                >
                                                                                    <X size={12} />
                                                                                </button>
                                                                            </div>
                                                                        ))}
                                                                        <button className={cn + '__btn --ghost --sm'} onClick={addNewFieldOption}>
                                                                            <Plus size={13} /> Add option
                                                                        </button>
                                                                    </div>
                                                                </div>
                                                            )}
                                                        </div>

                                                        <div className={cn + '__field-panel__footer'}>
                                                            <button className={cn + '__btn --ghost --sm'} onClick={() => setNewField(null)}>Cancel</button>
                                                            <button
                                                                className={cn + '__btn --primary --sm'}
                                                                onClick={commitField}
                                                                disabled={!canCommit}
                                                            >
                                                                <Plus size={14} /> Add field
                                                            </button>
                                                        </div>
                                                    </>
                                                );
                                            })()}
                                        </div>
                                    ) : (
                                        <div style={{ padding: '0 0.75rem' }}>
                                            <button
                                                className={cn + '__btn --ghost --sm'}
                                                onClick={() => setNewField({ pageIdx, stage: 'type', title: '', type: 'text', required: false, options: [], maxLength: 250, numberMin: '', numberMax: '' })}
                                            >
                                                <Plus size={14} /> Add field
                                            </button>
                                        </div>
                                    )}
                                </div>
                            ))}

                            <button className={cn + '__btn --ghost'} onClick={addPage}>
                                <Plus size={15} /> Add page
                            </button>
                        </div>
                    )}

                    {/* ── Step 4: Review ── */}
                    {step === 4 && (
                        <div className={cn + '__step'}>
                            <p className={cn + '__hint'}>Review your settings before creating the form.</p>

                            <div className={cn + '__review-section'}>
                                <div className={cn + '__review-section__title'}><FileText size={14} /> Basics</div>
                                <div className={cn + '__review-row'}><span>Name</span><strong>{draft.basics.title}</strong></div>
                                {draft.basics.description && <div className={cn + '__review-row'}><span>Description</span><em>{draft.basics.description}</em></div>}
                                {imagePreview && <div className={cn + '__review-row'}><span>Photo</span><strong>Uploaded ✓</strong></div>}
                            </div>

                            <div className={cn + '__review-section'}>
                                <div className={cn + '__review-section__title'}><CheckSquare2 size={14} /> Status Workflow</div>
                                <div className={cn + '__review-row'}>
                                    <span>Form Type</span>
                                    <strong>{FORM_TYPES.find(t => t.id === draft.formType)?.label || draft.formType}</strong>
                                </div>
                                <div className={cn + '__review-row --page'}>{draft.statusList.length} statuses</div>
                                {draft.statusList.map((s, si) => (
                                    <div key={si} className={cn + '__review-row --indent'} style={{ alignItems: 'center' }}>
                                        <div className={cn + '__review-statusDot'} style={{ background: s.color }} />
                                        <span>{s.title}</span>
                                    </div>
                                ))}
                            </div>

                            <div className={cn + '__review-section'}>
                                <div className={cn + '__review-section__title'}><Users size={14} /> Agents</div>
                                {draft.agents.length === 0
                                    ? <div className={cn + '__review-row --muted'}>No agents added</div>
                                    : draft.agents.map(a => (
                                        <div key={a.userId} className={cn + '__review-row'}><span>{a.fullName || `User ${a.userId}`}</span></div>
                                    ))
                                }
                            </div>

                            <div className={cn + '__review-section'}>
                                <div className={cn + '__review-section__title'}><Layout size={14} /> Fields</div>
                                {draft.pages.map((p, pi) => (
                                    <div key={p.tempId}>
                                        <div className={cn + '__review-row --page'}>{p.name} ({p.fields.length} field{p.fields.length !== 1 ? 's' : ''})</div>
                                        {p.fields.map(f => (
                                            <div key={f.tempId} className={cn + '__review-row --indent'}>
                                                <span>{f.title}</span>
                                                <em>{FIELD_TYPES.find(t => t.value === f.type)?.label}{f.required ? ' · Required' : ' · Optional'}{isChoiceType(f.type) && f.options?.length ? ` · ${f.options.length} options` : ''}</em>
                                            </div>
                                        ))}
                                    </div>
                                ))}
                            </div>

                            <div className={cn + '__review-section'}>
                                <div className={cn + '__review-section__title'}><Settings size={14} /> Advanced</div>
                                <div className={cn + '__review-row --muted'}>Advanced settings can be configured after the form is created.</div>
                            </div>
                        </div>
                    )}

                </div>

                {/* Navigation */}
                <div className={cn + '__main__footer'}>
                    {step > 1 && (
                        <button className={cn + '__btn --ghost'} onClick={handleBack}>
                            <ChevronLeft size={16} /> Back
                        </button>
                    )}
                    <div style={{ flex: 1 }} />
                    {step < 4 ? (
                        <button className={cn + '__btn --primary'} onClick={handleNext} disabled={!canAdvance()}>
                            Next <ChevronRight size={16} />
                        </button>
                    ) : (
                        <button className={cn + '__btn --confirm'} onClick={handleCreate}>
                            <CheckCircle2 size={16} /> Confirm &amp; Create
                        </button>
                    )}
                </div>
            </div>
        </div>
    );
}
